import React, { useState, useEffect } from 'react';
import ModalOverlay from '../../shared/ModalOverlay';
import { DistroList, DistroSchedule } from '../../types';
import { CARRIERS } from '../../config/carriers';

interface DistroScheduleModalProps {
  active: boolean;
  onClose: () => void;
  onSave: (schedule: DistroSchedule) => void;
  distro: DistroList | null;
}

const ALL_STATUSES = ['Complete', 'In Work', 'In Transit', 'In Planning', 'Scheduled', 'On Hold'];
const UNIT_LABELS: { [key: string]: string } = { daily: 'day(s)', weekly: 'week(s) on:', monthly: 'month(s)', yearly: 'year(s)' };
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const DistroScheduleModal: React.FC<DistroScheduleModalProps> = ({ active, onClose, onSave, distro }) => {
  const [pattern, setPattern] = useState('weekly');
  const [interval, setInterval] = useState(1);
  const [days, setDays] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [carrierFilter, setCarrierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  useEffect(() => {
    if (distro?.schedule) {
      const s = distro.schedule;
      setPattern(s.pattern || 'weekly');
      setInterval(s.interval || 1);
      setDays(s.days || []);
      setStartDate(s.start_date || '');
      setSenderEmail(s.sender_email || '');
      setCarrierFilter(s.carrier_filter || '');
      setStatusFilter(s.status_filter || []);
    } else {
      setPattern('weekly');
      setInterval(1);
      setDays([]);
      setStartDate('');
      setSenderEmail('');
      setCarrierFilter('');
      setStatusFilter([]);
    }
  }, [distro, active]);

  const toggleDay = (day: string) => {
    setDays((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]);
  };

  const toggleStatus = (status: string) => {
    setStatusFilter((prev) => prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]);
  };

  const handleSave = () => {
    onSave({ pattern, interval, days, start_date: startDate, sender_email: senderEmail, carrier_filter: carrierFilter, status_filter: statusFilter });
  };

  return (
    <ModalOverlay active={active} onClose={onClose} className="distro-sched-z">
      <div className="modal modal-narrow">
        <div className="modal-header">
          <h2>Schedule — {distro?.name || ''}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="sched-fields">
          <div className="form-group">
            <label>Recurrence Pattern</label>
            <div className="sched-pattern-row">
              {['daily', 'weekly', 'monthly', 'yearly'].map((p) => (
                <label key={p} className="sched-radio">
                  <input type="radio" name="sched-pattern" value={p} checked={pattern === p} onChange={() => setPattern(p)} />
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Recur Every</label>
            <div className="sched-recur-row">
              <input type="number" min={1} value={interval} onChange={(e) => setInterval(parseInt(e.target.value) || 1)} style={{ width: '60px' }} />
              <span className="sched-unit">{UNIT_LABELS[pattern] || 'unit(s)'}</span>
            </div>
          </div>

          <div className="form-group">
            <label>Days</label>
            <div className="sched-days-row">
              {DAY_KEYS.map((key, i) => (
                <label key={key} className="sched-day-check">
                  <input type="checkbox" checked={days.includes(key)} onChange={() => toggleDay(key)} />
                  {DAY_NAMES[i]}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Sender's Email</label>
            <input type="email" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} placeholder="e.g. andyhob@amazon.com" />
          </div>

          <div className="form-group">
            <label>Filters</label>
            <div className="sched-filter-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Carrier</label>
                <select value={carrierFilter} onChange={(e) => setCarrierFilter(e.target.value)}>
                  <option value="">All Carriers</option>
                  {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Status</label>
                <div className="sched-status-checks">
                  {ALL_STATUSES.map((s) => (
                    <label key={s} className="sched-day-check">
                      <input type="checkbox" checked={statusFilter.includes(s)} onChange={() => toggleStatus(s)} />
                      {s}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>Save Schedule</button>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default DistroScheduleModal;
